
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as entity from '../entity'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Source} from './Source';
import {FieldModelSource} from './Source';
import {AgentInfo} from '../entity/AgentInfo';
import {FieldModelAgentInfo} from '../entity/AgentInfo';

/**
 * TokenCreateParams struct
 */
class TokenCreateParams {
  /**
   * Initialize struct
   * @param {!number=} login
   * @param {!Source=} userType
   * @param {!AgentInfo=} agentInfo
   * @constructor
   */
  constructor (login = new UInt64(0, 0), userType = new Source(), agentInfo = new AgentInfo()) {
    this.Login = login
    this.UserType = userType
    this.AgentInfo = agentInfo
  }

  /**
   * Copy struct (shallow copy)
   * @this {!TokenCreateParams}
   * @param {!TokenCreateParams} other Other struct
   * @returns {!TokenCreateParams} This struct
   */
  copy (other) {
    if (other.Login != null) {
      this.Login = UInt64.fromNumber(other.Login)
    } else {
      this.Login = null
    }
    if (other.UserType != null) {
      this.UserType = Source.fromObject(other.UserType)
    } else {
      this.UserType = null
    }
    if (other.AgentInfo != null) {
      this.AgentInfo = AgentInfo.fromObject(other.AgentInfo)
    } else {
      this.AgentInfo = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!TokenCreateParams}
   * @returns {!TokenCreateParams} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new TokenCreateParamsModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new TokenCreateParamsModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!TokenCreateParams}
   * @param {!TokenCreateParams} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof TokenCreateParams)) {
      throw new TypeError('Instance of TokenCreateParams is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!TokenCreateParams}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Login: ((this.Login != null) ? this.Login.toNumber() : null),
      UserType: ((this.UserType != null) ? this.UserType : null),
      AgentInfo: ((this.AgentInfo != null) ? this.AgentInfo : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return TokenCreateParams.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!TokenCreateParams} other Object value
   * @returns {!TokenCreateParams} Created struct
   */
  static fromObject (other) {
    return new TokenCreateParams().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!TokenCreateParams}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return TokenCreateParams.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!TokenCreateParams}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 14101
  }
}

export { TokenCreateParams };

/**
 * Fast Binary Encoding TokenCreateParams field model
 */
class FieldModelTokenCreateParams extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Login = new fbe.FieldModelUInt64(buffer, 4 + 4)
    this.UserType = new FieldModelSource(buffer, this.Login.fbeOffset + this.Login.fbeSize)
    this.AgentInfo = new FieldModelAgentInfo(buffer, this.UserType.fbeOffset + this.UserType.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelTokenCreateParams}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelTokenCreateParams}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Login.fbeSize + this.UserType.fbeSize + this.AgentInfo.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelTokenCreateParams}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Login.fbeExtra + this.UserType.fbeExtra + this.AgentInfo.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelTokenCreateParams}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelTokenCreateParams.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelTokenCreateParams}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 14101
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelTokenCreateParams}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelTokenCreateParams}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Login.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Login.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Login.fbeSize

    if ((fbeCurrentSize + this.UserType.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.UserType.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.UserType.fbeSize

    if ((fbeCurrentSize + this.AgentInfo.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.AgentInfo.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AgentInfo.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelTokenCreateParams}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelTokenCreateParams}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelTokenCreateParams}
   * @param {!TokenCreateParams} fbeValue Default value, defaults is new TokenCreateParams()
   * @returns {!TokenCreateParams} TokenCreateParams value
   */
  get (fbeValue = new TokenCreateParams()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelTokenCreateParams}
   * @param {!TokenCreateParams} fbeValue TokenCreateParams value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Login.fbeSize) <= fbeStructSize) {
      fbeValue.Login = this.Login.get()
    } else {
      fbeValue.Login = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Login.fbeSize

    if ((fbeCurrentSize + this.UserType.fbeSize) <= fbeStructSize) {
      fbeValue.UserType = this.UserType.get()
    } else {
      fbeValue.UserType = new Source()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.UserType.fbeSize

    if ((fbeCurrentSize + this.AgentInfo.fbeSize) <= fbeStructSize) {
      fbeValue.AgentInfo = this.AgentInfo.get()
    } else {
      fbeValue.AgentInfo = new AgentInfo()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AgentInfo.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelTokenCreateParams}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelTokenCreateParams}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelTokenCreateParams}
   * @param {!TokenCreateParams} fbeValue TokenCreateParams value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelTokenCreateParams}
   * @param {TokenCreateParams} fbeValue TokenCreateParams value
   */
  setFields (fbeValue) {
    this.Login.set(fbeValue.Login)
    this.UserType.set(fbeValue.UserType)
    this.AgentInfo.set(fbeValue.AgentInfo)
  }
}

export { FieldModelTokenCreateParams };

/**
 * Fast Binary Encoding TokenCreateParams model
 */
class TokenCreateParamsModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelTokenCreateParams(this.buffer, 4)
  }

  /**
   * Get the TokenCreateParams model
   * @this {!TokenCreateParamsModel}
   * @returns {!FieldModelTokenCreateParams} model TokenCreateParams model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!TokenCreateParamsModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!TokenCreateParamsModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return TokenCreateParamsModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!TokenCreateParamsModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelTokenCreateParams.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!TokenCreateParamsModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!TokenCreateParamsModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!TokenCreateParamsModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!TokenCreateParamsModel}
   * @param {!TokenCreateParams} value TokenCreateParams value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!TokenCreateParamsModel}
   * @param {!TokenCreateParams} value TokenCreateParams value, defaults is new TokenCreateParams()
   * @return {!object} Deserialized TokenCreateParams value and its size
   */
  deserialize (value = new TokenCreateParams()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new TokenCreateParams(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new TokenCreateParams(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!TokenCreateParamsModel}
   * @param {!number} prev Previous TokenCreateParams model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { TokenCreateParamsModel };
